import { useState, useEffect } from "react";
import { createFilterOptions, getId, isValidId, updateActiveTask, Util } from '../../util/Util'
import { isDueDateValidForSupply } from "./supplyHelpers";
import RefreshButton from "../../components/RefreshButton";
import { useSupply } from "./SupplyContext";
import { useUI } from "../../providers/MoleUIContext";
import { useAppSocket } from "../../providers/AppSocketContext";
import { DepotEnum, MIN_DATE, MYSQL_LIMITS, OperationEnum } from "../../util/constants";
import { useAuth } from "../../hooks/useAuth";
import { Autocomplete, Grid, TextField } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "../../components/MoleDatePicker";
import { businessTodayDate, parseISODay } from "../../util/businessClock";

interface Props {
	setValidateCheck: (value: boolean) => void;
}

interface Destination {
	id: number;
	name: string;
}

interface Source {
	id: number;
	name: string;
	destinations: Destination[];
}

interface OperationGroup {
	operation: {
		id: number;
		name: string;
	};
	sources: Source[];
}

const SupplyDetail = ({ setValidateCheck }: Props) => {
	const [mapping, setMapping] = useState<OperationGroup[]>([]);
	const [selectedOperationId, setSelectedOperationId] = useState<number>(-1);
	const [selectedSourceId, setSelectedSourceId] = useState<number>(-1);
	const [isClientLoading, setIsClientLoading] = useState<boolean>(false);
	const [isSupplierLoading, setIsSupplierLoading] = useState<boolean>(false);
	const [isDepotLoading, setIsDepotLoading] = useState<boolean>(false);
	const [errorInput, setErrorInput] = useState("");
	const [clients, setClients] = useState<any[]>([]);
	const [suppliers, setSuppliers] = useState<any[]>([]);

	const { setPendingTasks, activeTask } = useSupply();
	const { appSettings } = useAppSocket();
	const { queueReRun, t } = useUI();
	const { api } = useAuth();

	const selectedOperation = mapping.find(op => op.operation.id === selectedOperationId);
	const sources = selectedOperation?.sources || [];
	const selectedSource = sources.find(src => src.id === selectedSourceId);
	const destinations = selectedSource?.destinations || [];
	const minDate = appSettings?.SYSTEM_INCEPTION_DATE?.value || MIN_DATE;

	const fetchClients = async () => {
		setIsClientLoading(true);

		const res = await api.get("/clients",
			{
				params: {
					Active: true,
					Type: "WORKER"
				}
			}
		);

		if (res) {
			if (res.status === 200) {
				res.data.sort((a: any, b: any) => a.acc_name.localeCompare(b.acc_name));
				setClients(res.data);
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchClients);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setIsClientLoading(false);
	};

	const fetchSuppliers = async () => {
		setIsSupplierLoading(true);

		const res = await api.get("/supplier");

		if (res) {
			if (res.status === 200) {
				res.data.sort((a: any, b: any) => a.sup_name.localeCompare(b.sup_name));
				setSuppliers(res.data);
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchSuppliers);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setIsSupplierLoading(false);
	};

	const fetchDepots = async () => {
		setIsDepotLoading(true);

		const res = await api.get("/operations/maps",
			{
				params: {
					Type: "Entry"
				}
			}
		);

		if (res) {
			if (res.status === 200) {
				setMapping(res.data);
			} else if (res.status === 401 || res.status === 422) { // TODO or status is in the 400 range
				queueReRun(fetchDepots);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setIsDepotLoading(false);
	};

	const onReceiverChanged = (receiver: number) => {
		updateActiveTask(setPendingTasks, { receiver });
	};

	const onRefChanged = (reference: string = Util.getSupplyNumber()) => {
		updateActiveTask(setPendingTasks, { reference });
	};

	const onDateChanged = (taskDate: Date = businessTodayDate()) => {
		const date = Util.formatISODate(taskDate);
		const updates: { date: string; due_date?: string | null } = { date };
		if (
			activeTask?.due_date
			&& !isDueDateValidForSupply(date, activeTask.due_date)
		) {
			updates.due_date = null;
		}
		updateActiveTask(setPendingTasks, updates);
	};

	const onDueDateChanged = (dueDate: Date | null) => {
		if (!dueDate || Util.compareStrings(dueDate.toString(), "Invalid Date")) {
			updateActiveTask(setPendingTasks, { due_date: null });
			return;
		}

		const due_date = Util.formatISODate(dueDate);
		if (!isDueDateValidForSupply(activeTask?.date, due_date)) {
			setErrorInput("dueDateInput");
			setTimeout(() => setErrorInput(""), 1500);
			return;
		}

		updateActiveTask(setPendingTasks, { due_date });
	};

	const onSupplierChanged = (supId: number) => {
		updateActiveTask(setPendingTasks, { supplier: supId });
	};

	const onDepotChanged = (depotId: number) => {
		updateActiveTask(setPendingTasks, { depot: depotId });
	};

	useEffect(() => {
		fetchClients();
		fetchSuppliers();
		fetchDepots();
	}, []);

	useEffect(() => {
		if (clients.length > 0) {
			const receiverId = getId(clients, activeTask?.receiver);
			if (receiverId !== activeTask?.receiver) {
				onReceiverChanged(receiverId);
			}
		}
	}, [activeTask, clients]);

	useEffect(() => {
		if (mapping.length > 0) {
			setSelectedOperationId(OperationEnum.ENTRY);
			setSelectedSourceId(DepotEnum.EXTERNAL_IN);
		}
	}, [mapping]);

	useEffect(() => {
		if (destinations.length > 0) {
			onDepotChanged(getId(destinations, activeTask?.depot));
		}
	}, [destinations]);

	useEffect(() => {
		if (suppliers.length > 0) {
			onSupplierChanged(getId(suppliers, activeTask?.supplier));
		}
	}, [suppliers]);

	useEffect(() => {
		const isValid = isValidId(clients, activeTask?.receiver) &&
			isValidId(suppliers, activeTask?.supplier) &&
			isValidId(destinations, activeTask?.depot) &&
			isDueDateValidForSupply(activeTask?.date, activeTask?.due_date);

		setValidateCheck(isValid);
	}, [activeTask, clients, suppliers, destinations]);

	return (
		<Grid container spacing={0}>
			<Grid size={{ xs: 6, sm: 4, md: 2 }} >
				<Autocomplete
					id="depotInput"
					size="small"
					options={destinations}
					filterOptions={createFilterOptions((opt) => opt.name || "")}
					getOptionLabel={(depot) => depot.name || ""}
					value={destinations.find((d) => d.id === activeTask?.depot) || null}
					onChange={(_, newValue) => {
						onDepotChanged(newValue?.id || -1);
					}}
					loading={isDepotLoading}
					autoHighlight
					autoComplete
					autoSelect
					disabled={!activeTask || isDepotLoading}
					renderInput={(params) => (
						<TextField
							{...params}
							label={
								<span>
									{t("common:ui.labels.depot")}&nbsp;&nbsp;
									<RefreshButton isWorking={isDepotLoading} onClick={fetchDepots} disabled={isDepotLoading} />
								</span>
							}
							variant="filled"
							fullWidth
						/>
					)}
				/>
			</Grid>
			<Grid size={{ xs: 6, sm: 4, md: 2 }} >
				<Autocomplete
					id="supplierInput"
					size="small"
					options={suppliers}
					filterOptions={createFilterOptions((opt) => opt.sup_name || "")}
					getOptionLabel={(supplier) => supplier.sup_name || ""}
					value={suppliers.find((s) => s.id === activeTask?.supplier) || null}
					onChange={(_, newValue) => {
						onSupplierChanged(newValue?.id || -1);
					}}
					loading={isSupplierLoading}
					autoHighlight
					autoComplete
					autoSelect
					disabled={!activeTask || isSupplierLoading}
					renderInput={(params) => (
						<TextField
							{...params}
							label={
								<span>
									{t("common:supplyDetail.supplier")}&nbsp;&nbsp;
									<RefreshButton isWorking={isSupplierLoading} onClick={fetchSuppliers} disabled={isSupplierLoading} />
								</span>
							}
							variant="filled"
							fullWidth
						/>
					)}
				/>
			</Grid>
			<Grid size={{ xs: 6, sm: 4, md: 2 }} >
				<Autocomplete
					id="clientInput"
					size="small"
					options={clients}
					filterOptions={createFilterOptions((opt) => opt.acc_name || "")}
					getOptionLabel={(client) => client.acc_name || ""}
					value={clients.find((c) => c.id === activeTask?.receiver) || null}
					onChange={(_, newValue) => {
						onReceiverChanged(newValue?.id || -1);
					}}
					loading={isClientLoading}
					autoHighlight
					autoComplete
					autoSelect
					disabled={!activeTask || isClientLoading}
					renderInput={(params) => (
						<TextField
							{...params}
							label={
								<span>
									{t("common:supplyDetail.receiver")}&nbsp;&nbsp;
									<RefreshButton isWorking={isClientLoading} onClick={fetchClients} disabled={isClientLoading} />
								</span>
							}
							variant="filled"
							fullWidth
						/>
					)}
				/>
			</Grid>
			<Grid size={{ xs: 6, sm: 4, md: 2 }} >
				<TextField
					id="referenceInput"
					size="small"
					label={
						<span>
							{t("common:supplyDetail.reference")}&nbsp;&nbsp;
							<RefreshButton isWorking={false} onClick={onRefChanged} />
						</span>
					}
					variant="filled"
					fullWidth
					type="text"
					value={activeTask?.reference || ""}
					inputProps={{ maxLength: MYSQL_LIMITS.VARCHAR(50).maxLength }}
					onBlur={(e) => {
						if (!e.target.value || e.target.value.trim() === "") {
							onRefChanged();
						}
					}}
					onChange={(e) => onRefChanged(e.target.value.trim())}
					disabled={!activeTask}
				/>
			</Grid>
			<Grid size={{ xs: 6, sm: 4, md: 2 }} >
				<LocalizationProvider dateAdapter={AdapterDateFns}>
					<DatePicker
						label={
							<span>
								{t("common:ui.labels.date")}&nbsp;&nbsp;
								<RefreshButton isWorking={false} onClick={onDateChanged} />
							</span>
						}
						value={activeTask?.date ? parseISODay(activeTask?.date) as Date : businessTodayDate()}
						minDate={(parseISODay(minDate) as Date)}
						onChange={(newDate) => {
							if (!newDate || Util.compareStrings(newDate.toString(), "Invalid Date") || newDate < (parseISODay(minDate) as Date)) {
								setErrorInput("dateInput");
								setTimeout(() => setErrorInput(""), 1500);
								return;
							}
							const date = newDate instanceof Date ? newDate : newDate.toDate(); // ✅ safe narrowing
							onDateChanged(date);
						}}
						disabled={!activeTask}
						slotProps={{
							textField: {
								id: "dateInput",
								size: "small",
								fullWidth: true,
								variant: "filled", // ✅ makes the textfield filled
								error: errorInput === "dateInput", // ✅ built-in error state
								helperText:
									errorInput === "dateInput" ? t("common:validation.invalidDate") : "", // ✅ error message
							},
						}}
					/>
				</LocalizationProvider>
			</Grid>
			<Grid size={{ xs: 6, sm: 4, md: 2 }} >
				<LocalizationProvider dateAdapter={AdapterDateFns}>
					<DatePicker
						label={t("common:supplyDetail.dueDate")}
						value={activeTask?.due_date ? parseISODay(activeTask.due_date) as Date : null}
						minDate={activeTask?.date ? parseISODay(activeTask.date) as Date : (parseISODay(minDate) as Date)}
						onChange={(newDate) => {
							if (!newDate) {
								onDueDateChanged(null);
								return;
							}
							if (Util.compareStrings(newDate.toString(), "Invalid Date")) {
								onDueDateChanged(null);
								return;
							}
							const date = newDate instanceof Date ? newDate : newDate.toDate();
							onDueDateChanged(date);
						}}
						disabled={!activeTask}
						slotProps={{
							textField: {
								id: "dueDateInput",
								size: "small",
								fullWidth: true,
								variant: "filled",
								error: errorInput === "dueDateInput",
								helperText:
									errorInput === "dueDateInput"
										? t("common:validation.dueDateBeforeSupplyDate")
										: "",
							},
							field: {
								clearable: true,
							},
						}}
					/>
				</LocalizationProvider>
			</Grid>
		</Grid>
	);
};

export default SupplyDetail;